import {
  CustomOptionsStatus,
  ExternalMaskingDetectorSettingsInput,
  GuardrailSettingsState,
  RegexFlag,
  RegexMaskingDetectorSettings,
} from '../../../../../gamma-api';
import { isValidJSON } from '../../../../utils/stringUtil';
import { CheckFieldValueFnMap } from '../../../../utils/validationUtil';
import { validateRegexPatterns } from '../shared/regexValidation';
import {
  GuardrailDetectorSettings,
  IMaskingDetectorStep,
  isSettingsEnabled,
  MaskingDetectorTypeValue,
} from '../shared/utils';

export type DetectorValidationForm = {
  type?: MaskingDetectorTypeValue;
  state?: GuardrailSettingsState;
  apiUrl?: string;
  patterns?: string[];
  flags?: RegexFlag[];
  customOptionsStatus?: CustomOptionsStatus;
  customOptionsJson?: string;
};

export type DetectorValidationField = keyof DetectorValidationForm;

const isValidationActive = (allFormValues: DetectorValidationForm) => isSettingsEnabled(allFormValues.state);

export const detectorFieldValueFnMap: CheckFieldValueFnMap<DetectorValidationForm> = {
  type: () => true,
  state: () => true,
  apiUrl: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== MaskingDetectorTypeValue.External) {
      return true;
    }

    return !!value?.trim();
  },
  patterns: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== MaskingDetectorTypeValue.Regex) {
      return true;
    }

    return validateRegexPatterns(value, allFormValues.flags);
  },
  flags: () => true,
  customOptionsStatus: () => true,
  customOptionsJson: (value, allFormValues) => {
    if (
      !isValidationActive(allFormValues) ||
      allFormValues.type !== MaskingDetectorTypeValue.External ||
      allFormValues.customOptionsStatus !== CustomOptionsStatus.Enabled
    ) {
      return true;
    }

    return isValidJSON(value, { allowEmpty: true, objectOnly: true });
  },
};

const FIELD_ORDER_BY_TYPE: Record<MaskingDetectorTypeValue, DetectorValidationField[]> = {
  [MaskingDetectorTypeValue.External]: ['apiUrl', 'customOptionsJson'],
  [MaskingDetectorTypeValue.Regex]: ['patterns'],
};

export const getFirstInvalidDetectorField = (values: DetectorValidationForm): DetectorValidationField | undefined => {
  if (!values.type) {
    return undefined;
  }

  return FIELD_ORDER_BY_TYPE[values.type].find(
    (field) => detectorFieldValueFnMap[field](values[field], values) !== true
  );
};

export const buildDetectorValidationForm = (step?: IMaskingDetectorStep): DetectorValidationForm => {
  const detector = step?.detector as GuardrailDetectorSettings | undefined;

  if (detector?.type === MaskingDetectorTypeValue.External) {
    const externalDetector = detector as ExternalMaskingDetectorSettingsInput;

    return {
      type: MaskingDetectorTypeValue.External,
      state: externalDetector.state,
      apiUrl: externalDetector.api_url,
      patterns: [],
      flags: [],
      customOptionsStatus: externalDetector.custom_options?.status,
      customOptionsJson: externalDetector.custom_options?.json_value,
    };
  }

  if (detector?.type === MaskingDetectorTypeValue.Regex) {
    const regexDetector = detector as RegexMaskingDetectorSettings;

    return {
      type: MaskingDetectorTypeValue.Regex,
      state: regexDetector.state,
      apiUrl: '',
      patterns: regexDetector.patterns,
      flags: regexDetector.flags,
      customOptionsStatus: CustomOptionsStatus.Disabled,
      customOptionsJson: '',
    };
  }

  return {
    type: undefined,
    state: GuardrailSettingsState.Enabled,
    apiUrl: '',
    patterns: [],
    flags: [],
    customOptionsStatus: CustomOptionsStatus.Disabled,
    customOptionsJson: '',
  };
};
